import { useMemo } from "react";
import useAPI, { useAPIMutation } from "@/services/api/hook";
import {
  TermsAmendmentRequest,
  TermsCorrectionRequest,
  TermsFieldSettings,
  TermsLineEditability,
  TermsPreviewRequest,
  TermsPreviewResponse
} from "./types";

// Every Update terms route is addressed by the contract it acts on.
interface TermsPathParams {
  contractId: string;
}

interface UseTermsFieldSettingsOptions {
  enabled?: boolean;
}

// The Update terms gate plus per-service field editability, in one read.
export const useTermsFieldSettings = (
  contractId: string | undefined,
  options: UseTermsFieldSettingsOptions = {}
) => {
  const result = useAPI(
    "contracts/terms/fieldSettings",
    { pathParams: { contractId: contractId ?? "" } },
    { enabled: options.enabled !== false && !!contractId }
  );

  return {
    ...result,
    data: result.data as TermsFieldSettings | undefined
  };
};

interface TermsPreviewMutationData {
  pathParams: TermsPathParams;
  data: TermsPreviewRequest;
}

// The footer's New column, computed by the same code the save runs.
export const useTermsPreview = (contractId: string | undefined) =>
  useAPIMutation<TermsPreviewMutationData, TermsPreviewResponse>(
    "contracts/terms/preview",
    { pathParams: { contractId: contractId ?? "" } }
  );

interface TermsAmendmentMutationData {
  pathParams: TermsPathParams;
  data: TermsAmendmentRequest;
}

interface TermsCorrectionMutationData {
  pathParams: TermsPathParams;
  data: TermsCorrectionRequest;
}

// Both saves answer with the contract they wrote.
interface TermsSaveResponse {
  contractId: string;
}

// An amendment writes a version; a correction overwrites the live terms and writes none.
export const useSaveTermsAmendment = (contractId: string | undefined) =>
  useAPIMutation<TermsAmendmentMutationData, TermsSaveResponse>(
    "contracts/terms/amendments",
    { pathParams: { contractId: contractId ?? "" } }
  );

export const useSaveTermsCorrection = (contractId: string | undefined) =>
  useAPIMutation<TermsCorrectionMutationData, TermsSaveResponse>(
    "contracts/terms/corrections",
    { pathParams: { contractId: contractId ?? "" } }
  );

// The settings describe only services already on the contract, so silence about
// a line leaves it to the panel's own rules rather than locking it.
const UNLOCKED_LINE: TermsLineEditability = {
  isPriceEditable: true,
  isBillingFrequencyEditable: true
};

// Per-line editability from the field settings; only an explicit false locks.
export const useTermsLineEditability = (
  settings: TermsFieldSettings | undefined
) =>
  useMemo(() => {
    const byServiceItem = new Map(
      (settings?.serviceItems ?? []).map((item) => [item.serviceItemId, item])
    );

    return (
      serviceItemId: string | null | undefined,
      serviceItemPricingId: string | null | undefined
    ): TermsLineEditability => {
      if (!serviceItemId) return UNLOCKED_LINE;
      const serviceItem = byServiceItem.get(serviceItemId);
      if (!serviceItem) return UNLOCKED_LINE;
      const pricingOption = serviceItemPricingId
        ? serviceItem.pricingOptions.find(
            (option) => option.pricingId === serviceItemPricingId
          )
        : undefined;
      return {
        isPriceEditable: serviceItem.isPriceEditable,
        isBillingFrequencyEditable:
          pricingOption?.isBillingFrequencyEditable ?? true
      };
    };
  }, [settings]);
